import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setToken } from '../lib/api';
import { BrandLogo } from '../components/BrandLogo';

// Turnstile applies only to public (proxied) access; LAN-direct is trusted.
const IS_PUBLIC = !/^(localhost|127\.|192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(window.location.hostname);

// Our API errors arrive as JSON strings ({"error":"..."}); surface the message.
function parseErr(e: any): string {
  try { return JSON.parse(e.message).error || e.message; } catch { return e?.message || ''; }
}

// Second-factor step state: null = password screen; otherwise we're collecting
// a TOTP code (enroll shows the QR to scan first, verify just asks for a code).
type MfaStep = { flow: 'verify' | 'enroll'; token: string; qr?: string | null; secret?: string } | null;

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tsToken, setTsToken] = useState('');
  const [siteKey, setSiteKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState<MfaStep>(null);
  const [code, setCode] = useState('');
  // One-time backup codes returned on enrollment confirmation (#941) — shown
  // exactly once here, then the user continues into the app.
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const navigate = useNavigate();

  // Turnstile is per-instance: only enforced when the backend supplies a site key (#157).
  const needsTs = IS_PUBLIC && !!siteKey;

  // OIDC callback support: token in URL -> store and redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oidcToken = params.get('oidc_token');
    if (oidcToken) {
      setToken(oidcToken);
      window.history.replaceState({}, '', '/');
      navigate('/');
    }
  }, [navigate]);

  // Fetch this instance's Turnstile site key (#157). Empty -> Turnstile disabled for this instance.
  useEffect(() => {
    if (!IS_PUBLIC) return;
    fetch('/api/auth/config')
      .then(r => r.json())
      .then(c => setSiteKey(c.turnstile_site_key || ''))
      .catch(() => {});
  }, []);

  // Load + render Cloudflare Turnstile (public access + a configured site key only)
  useEffect(() => {
    if (!needsTs) return;
    function render() {
      const w: any = window;
      const el = document.getElementById('ts-widget');
      if (w.turnstile && el && !el.hasChildNodes()) {
        w.turnstile.render('#ts-widget', {
          sitekey: siteKey,
          callback: (t: string) => setTsToken(t),
          'expired-callback': () => setTsToken(''),
          'error-callback': () => setTsToken(''),
        });
      }
    }
    const w: any = window;
    if (w.turnstile) { render(); return; }
    w.__tsonload = render;
    if (!document.getElementById('cf-turnstile-script')) {
      const s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__tsonload';
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
  }, [needsTs, siteKey]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (needsTs && !tsToken) { setError('Please complete the verification'); return; }
    setLoading(true); setError('');
    try {
      const r = await api.login(username, password, tsToken);
      if (r.mfa && r.mfa_token) {
        // Password accepted — advance to the second factor instead of a session.
        setMfa({ flow: r.mfa, token: r.mfa_token, qr: r.qr_data_url, secret: r.secret });
        setCode('');
      } else if (r.token) {
        setToken(r.token);
        navigate('/');
      } else {
        setError('login failed');
      }
    } catch (e: any) {
      setError(parseErr(e) || 'login failed');
      const w: any = window;
      if (needsTs && w.turnstile) w.turnstile.reset('#ts-widget');
      setTsToken('');
    } finally {
      setLoading(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!mfa) return;
    setLoading(true); setError('');
    try {
      const r = await api.mfaVerify(mfa.token, code.trim());
      setToken(r.token);
      // Enrollment confirmation hands back the one-time backup codes (#941):
      // pause on a save-these screen before entering the app.
      if (r.backup_codes?.length) setBackupCodes(r.backup_codes);
      else navigate('/');
    } catch (e: any) {
      setError(parseErr(e) || 'verification failed');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-96 space-y-4">
        <div className="flex justify-center"><BrandLogo size={56} iconOnly /></div>
        <div className="text-center">
          <div className="text-2xl font-semibold tracking-tight">Stratus VoIP</div>
          <div className="text-xs uppercase tracking-widest text-textSecondary">Cloud-native PBX</div>
        </div>
        <div className="card">
        {backupCodes ? (<>
          <h1 className="text-base font-medium mb-1 text-center">Save your backup codes</h1>
          <p className="text-sm text-textSecondary text-center mb-3">If you lose your authenticator, any one of these codes signs you in once. Store them somewhere safe — they will not be shown again.</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm text-center mb-4">
            {backupCodes.map(c => <div key={c} className="text-textPrimary">{c}</div>)}
          </div>
          <button type="button" className="btn-primary w-full" onClick={() => { setBackupCodes(null); navigate('/'); }}>
            I've saved my backup codes
          </button>
        </>) : !mfa ? (<>
          <h1 className="text-base font-medium mb-4 text-center">Sign in</h1>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">Email or extension</label>
            <input className="input" value={username} onChange={e => setUsername(e.target.value)} placeholder="you@email.com or your extension" autoFocus />
            <p className="text-xs text-textSecondary mt-1">Sign in with your email address or your extension number.</p>
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Password</label>
            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {needsTs && <div id="ts-widget" className="flex justify-center"></div>}
          {error && <div className="text-danger text-sm">{error}</div>}
          <button type="submit" disabled={loading || (needsTs && !tsToken)} className="btn-primary w-full">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="mt-4 pt-4 border-t border-border text-center text-sm">
          <a href="/api/auth/oidc/m365/start?domain_uuid=" className="text-primary hover:underline">Sign in with Microsoft 365</a>
          <span className="mx-2 text-textSecondary">·</span>
          <a href="/api/auth/oidc/google/start?domain_uuid=" className="text-primary hover:underline">Sign in with Google</a>
        </div>
        </>) : (<>
          <h1 className="text-base font-medium mb-1 text-center">Two-factor authentication</h1>
          {mfa.flow === 'enroll' ? (
            <div className="space-y-3">
              <p className="text-sm text-textSecondary text-center">Set up an authenticator app to finish signing in. Scan this code with Google Authenticator, Authy, or 1Password.</p>
              {mfa.qr && <div className="flex justify-center"><img src={mfa.qr} alt="Authenticator QR code" className="rounded bg-white p-2" width={200} height={200} /></div>}
              {mfa.secret && (
                <p className="text-xs text-textSecondary text-center">Can't scan? Enter this key manually:<br /><code className="text-textPrimary break-all">{mfa.secret}</code></p>
              )}
            </div>
          ) : (
            <p className="text-sm text-textSecondary text-center mb-2">Enter the 6-digit code from your authenticator app — or one of your backup codes if you've lost it.</p>
          )}
          <form onSubmit={onVerify} className="space-y-3 mt-3">
            <input
              className="input text-center tracking-[0.3em] text-lg font-mono"
              value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 11))}
              placeholder="000000" autoComplete="one-time-code" autoFocus maxLength={11}
            />
            {error && <div className="text-danger text-sm">{error}</div>}
            <button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full">
              {loading ? 'Verifying…' : (mfa.flow === 'enroll' ? 'Confirm & sign in' : 'Verify')}
            </button>
            <button type="button" className="btn-ghost w-full text-sm" onClick={() => { setMfa(null); setError(''); setPassword(''); }}>
              Back to sign in
            </button>
          </form>
        </>)}
        </div>
      </div>
    </div>
  );
}
